import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';

import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CatalogOthersType } from '../Common/CatalogOthersType';
import { CatalogPageToolbar } from '../Common/CatalogPageToolbar';
import { useRemoveOtherVariable } from '../Common/CatalogRemoveOtherVariable/hooks/useRemoveOtherVariable';
import { CatalogTable } from '../Common/CatalogTable';
import { useCommonTableData, useCommonVariablesColumns, useCommonVariablesFilters } from '../Common/CatalogTable/hooks';
import { CatalogTablePage } from '../Common/CatalogTablePage';
import { VariableGroups } from '../Common/CatalogVariableGroups';
import { CreateOrEditOtherVariable } from '../Common/CreateOrEditOtherVariable';
import { ViewOtherVariable } from '../Common/CreateOrEditOtherVariable/ViewOtherVariable';
import type { VariableIdType } from '../Common/types';

export const CatalogVariablesOtherBIM = () => {
    const [currentVariable, setCurrentVariable] = useState<VariableIdType>();
    const [isOpenVariableGroups, setIsOpenVariableGroups] = useState<boolean>(false);
    const [isOpenOtherTypeModal, setIsOpenOtherTypeModal] = useState<boolean>(false);
    const [currentViewVariableId, setCurrentViewVariableId] = useState<number | undefined>();

    const { id } = useParams();

    const { catalogs: catalogsRules } = useAccess();

    const {
        catalogName,
        catalogOtherCostVariablesData,
        currentPage,
        setCurrentPage,
        currentPageSize,
        setCurrentPageSize,
        totalPages,
        setQueryWhere,
        setQueryOrder,
        updateCatalogVariablesOther,
        isLoading,
        isError,
    } = useCommonTableData({
        catalogId: id,
        globalOnly: false,
        methodology: ProjectMethodologyEnum.Bim,
    });

    const { isOpenConfirmationDialog, handleDelete, handleOpenRemoveDialog, handleCloseConfirmationDialog } =
        useRemoveOtherVariable({
            updateCatalogVariables: updateCatalogVariablesOther,
        });

    const handleOpenCreateOrEditDialog = useCallback(
        (variableId: VariableIdType) => () => {
            setCurrentVariable(variableId);
        },
        [],
    );

    const handleCloseCreateOrEditDialog = useCallback(() => {
        setCurrentVariable(undefined);
    }, []);

    const handleOpenVariableGroups = useCallback(() => {
        setIsOpenVariableGroups(true);
    }, []);

    const handleCloseVariableGroups = useCallback(() => {
        setIsOpenVariableGroups(false);
    }, []);

    const toggleIsOpenOtherTypeModal = useCallback(() => {
        setIsOpenOtherTypeModal((p) => !p);
    }, []);

    const toggleOpenViewVariableDialog = useCallback(
        (variableId: number | undefined) => () => {
            setCurrentViewVariableId(variableId);
        },
        [],
    );
    const columns = useCommonVariablesColumns({
        onEdit: handleOpenCreateOrEditDialog,
        onView: toggleOpenViewVariableDialog,
        onRemove: handleOpenRemoveDialog,
        catalogsRules,
    });

    const filters = useCommonVariablesFilters({
        catalogId: id,
        globalOnly: false,
        methodology: ProjectMethodologyEnum.Bim,
    });

    return (
        <CatalogTablePage
            catalogName={catalogName}
            methodology={ProjectMethodologyEnum.Bim}
            isError={isError}
            isLoading={isLoading}
        >
            <CatalogPageToolbar
                handleOpenCreateOrEditDialog={handleOpenCreateOrEditDialog}
                handleOpenVariableGroups={handleOpenVariableGroups}
                handleOpenTypeModal={toggleIsOpenOtherTypeModal}
            />
            {catalogOtherCostVariablesData && (
                <CatalogTable
                    tableColumns={columns}
                    catalogOtherCostVariablesData={catalogOtherCostVariablesData as any}
                    currentPageSize={currentPageSize}
                    filters={filters}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    setCurrentPageSize={setCurrentPageSize}
                    setQueryOrder={setQueryOrder}
                    setQueryWhere={setQueryWhere}
                />
            )}
            {typeof currentVariable !== 'undefined' && typeof id !== 'undefined' && (
                <CreateOrEditOtherVariable
                    id={currentVariable}
                    catalogUniqueId={id}
                    onClose={handleCloseCreateOrEditDialog}
                    updateCatalogVariables={updateCatalogVariablesOther}
                    methodology={ProjectMethodologyEnum.Bim}
                />
            )}
            {typeof currentViewVariableId === 'number' && typeof id !== 'undefined' && (
                <ViewOtherVariable
                    id={currentViewVariableId}
                    onClose={toggleOpenViewVariableDialog(undefined)}
                    methodology={ProjectMethodologyEnum.Bim}
                />
            )}
            {isOpenConfirmationDialog && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleDelete}
                    onClose={handleCloseConfirmationDialog}
                    textHeader="Удаление переменной"
                    textConfirmation="Вы уверены, что хотите удалить переменную?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
            {isOpenVariableGroups && (
                <VariableGroups
                    onClose={handleCloseVariableGroups}
                    catalogUniqueId={id}
                    methodology={ProjectMethodologyEnum.Bim}
                />
            )}
            {isOpenOtherTypeModal && (
                <CatalogOthersType onClose={toggleIsOpenOtherTypeModal} methodology={ProjectMethodologyEnum.Bim} />
            )}
        </CatalogTablePage>
    );
};

CatalogVariablesOtherBIM.displayName = 'CatalogVariablesOtherBIM';
